import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  FormHelperText,
  Heading,
  Input,
  Text,
  Badge,
  SimpleGrid,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  VStack,
  useColorModeValue,
  useToast,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Card,
  CardHeader,
  CardBody,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdCheckCircle } from 'react-icons/md';
import {
  getAllCurrencies,
  saveCustomCurrency,
  deleteCustomCurrency,
  getDefaultCurrency,
  setDefaultCurrency,
  getDefaultVatRate,
  setDefaultVatRate,
  getCurrencySymbol,
  formatCurrency,
} from 'utils/currency';

export default function CurrencyManager() {
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'navy.800');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const textSubtle = useColorModeValue('gray.500', 'gray.400');
  const headerBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const rowHoverBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const previewBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const alertBg = useColorModeValue('blue.50', 'rgba(66, 153, 225, 0.12)');

  const [currencies, setCurrencies] = useState([]);
  const [defaultCurr, setCurrentDefaultCurr] = useState('EUR');
  const [defaultVat, setCurrentDefaultVat] = useState('7.00');

  // Modal for adding custom currency
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [newCurrency, setNewCurrency] = useState({
    code: '',
    symbol: '',
    name: '',
    flag: '🌐',
  });

  const loadData = () => {
    setCurrencies(getAllCurrencies());
    setCurrentDefaultCurr(getDefaultCurrency());
    setCurrentDefaultVat(getDefaultVatRate());
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveDefaults = () => {
    setDefaultCurrency(defaultCurr);
    setDefaultVatRate(defaultVat);
    toast({
      title: 'Defaults Updated',
      description: `Default platform currency set to ${defaultCurr} and VAT rate to ${defaultVat}%.`,
      status: 'success',
      duration: 3500,
      isClosable: true,
      position: 'top',
    });
    loadData();
  };

  const handleAddCustomCurrency = (e) => {
    e.preventDefault();
    if (!newCurrency.code.trim() || !newCurrency.name.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please enter both currency code and currency name.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    saveCustomCurrency(newCurrency);
    toast({
      title: 'Currency Added',
      description: `${newCurrency.code.toUpperCase()} has been added to available currencies.`,
      status: 'success',
      duration: 3500,
      isClosable: true,
      position: 'top',
    });

    setNewCurrency({ code: '', symbol: '', name: '', flag: '🌐' });
    onClose();
    loadData();
  };

  const handleDeleteCurrency = (code) => {
    if (code === defaultCurr) {
      toast({
        title: 'Cannot Delete Default',
        description: 'You cannot delete the currency that is currently set as platform default.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    deleteCustomCurrency(code);
    toast({
      title: 'Currency Removed',
      description: `${code} was removed from custom currencies.`,
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
    loadData();
  };

  const handleSetDefault = (code) => {
    setCurrentDefaultCurr(code);
    setDefaultCurrency(code);
    toast({
      title: 'Platform Default Changed',
      description: `${code} (${getCurrencySymbol(code)}) is now the default currency.`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    loadData();
  };

  return (
    <VStack spacing={6} align="stretch">
      {/* Top Banner / Guidance */}
      <Alert
        status="info"
        borderRadius="xl"
        variant="left-accent"
        bg={alertBg}
        borderColor="blue.500"
      >
        <AlertIcon />
        <Box>
          <AlertTitle fontWeight="bold" fontSize="md">
            Dynamic Multicurrency & Fiscal Engine
          </AlertTitle>
          <AlertDescription fontSize="sm" color={textSubtle}>
            Currencies configured here are instantly available when creating events.
            <strong> Note:</strong> Currency and Booking Fee apply exclusively to events with <em>Internal Ticketing</em>.
            When an event uses an <em>External Platform</em> (e.g. Eventbrite, Partner Platform), currency and fees are handled outside and hidden from the event editor.
          </AlertDescription>
        </Box>
      </Alert>

      {/* Global Defaults Configuration */}
      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" shadow="sm">
        <CardHeader pb={2}>
          <Heading size="md" color={textColor}>
            Platform Global Fiscal Defaults
          </Heading>
          <Text fontSize="sm" color={textSubtle}>
            Default settings applied when creating new events with internal ticket checkout.
          </Text>
        </CardHeader>
        <CardBody pt={2}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} alignItems="end">
            <FormControl>
              <FormLabel fontWeight="bold" fontSize="sm">
                Default Currency
              </FormLabel>
              <Select
                value={defaultCurr}
                onChange={(e) => setCurrentDefaultCurr(e.target.value)}
              >
                {currencies.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code} - {c.name}
                  </option>
                ))}
              </Select>
              <FormHelperText fontSize="xs" color={textSubtle}>
                Current Symbol: <strong>{getCurrencySymbol(defaultCurr)}</strong>
              </FormHelperText>
            </FormControl>

            <FormControl>
              <Flex justify="space-between" align="center" mb={1}>
                <FormLabel fontWeight="bold" fontSize="sm" mb={0}>
                  Default Ticket Booking Fee (%)
                </FormLabel>
                <Badge colorScheme="green" fontSize="xs" px={2} borderRadius="full">
                  Standard Fee (7%)
                </Badge>
              </Flex>
              <Input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={defaultVat}
                onChange={(e) => setCurrentDefaultVat(e.target.value)}
              />
              <FormHelperText fontSize="xs" color={textSubtle}>
                Default is 7.00% booking fee applied at checkout. Can be adjusted per event.
              </FormHelperText>
            </FormControl>

            <Button
              colorScheme="brand"
              onClick={handleSaveDefaults}
              leftIcon={<MdCheckCircle />}
              w={{ base: '100%', md: 'auto' }}
            >
              Save Fiscal Defaults
            </Button>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Currencies Catalog */}
      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" shadow="sm">
        <CardHeader pb={2}>
          <Flex justify="space-between" align="center" direction={{ base: 'column', sm: 'row' }} gap={3}>
            <Box>
              <Heading size="md" color={textColor}>
                Supported Currencies ({currencies.length})
              </Heading>
              <Text fontSize="sm" color={textSubtle}>
                Currencies available in Event creation for ticket pricing, checkout and invoicing.
              </Text>
            </Box>
            <Button
              leftIcon={<MdAdd />}
              colorScheme="purple"
              size="sm"
              onClick={onOpen}
            >
              Add Custom Currency
            </Button>
          </Flex>
        </CardHeader>

        <CardBody pt={2} px={0}>
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg={headerBg}>
                <Tr>
                  <Th>Currency</Th>
                  <Th>Symbol</Th>
                  <Th>Name</Th>
                  <Th>Example Price (100.00)</Th>
                  <Th>Type</Th>
                  <Th textAlign="right">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {currencies.map((c) => {
                  const isDefault = c.code === defaultCurr;
                  return (
                    <Tr key={c.code} _hover={{ bg: rowHoverBg }}>
                      <Td fontWeight="bold">
                        <HStack spacing={2}>
                          <Text fontSize="lg">{c.flag || '🌐'}</Text>
                          <Text>{c.code}</Text>
                          {isDefault && (
                            <Badge colorScheme="green" fontSize="10px">
                              DEFAULT
                            </Badge>
                          )}
                        </HStack>
                      </Td>
                      <Td>
                        <Badge colorScheme="purple" fontSize="xs" px={2} py={0.5} borderRadius="md">
                          {c.symbol}
                        </Badge>
                      </Td>
                      <Td color={textSubtle}>{c.name}</Td>
                      <Td fontWeight="semibold">{formatCurrency(100, c.code)}</Td>
                      <Td>
                        {c.isCustom ? (
                          <Badge colorScheme="orange" fontSize="xs">
                            Custom Added
                          </Badge>
                        ) : (
                          <Badge colorScheme="blue" fontSize="xs">
                            System Predefined
                          </Badge>
                        )}
                      </Td>
                      <Td textAlign="right">
                        <HStack spacing={2} justify="flex-end">
                          {!isDefault && (
                            <Button
                              size="xs"
                              variant="outline"
                              colorScheme="brand"
                              onClick={() => handleSetDefault(c.code)}
                            >
                              Make Default
                            </Button>
                          )}
                          {c.isCustom && (
                            <IconButton
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              icon={<MdDelete />}
                              aria-label="Delete custom currency"
                              onClick={() => handleDeleteCurrency(c.code)}
                            />
                          )}
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </CardBody>
      </Card>

      {/* Modal: Add Custom Currency */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl">
          <form onSubmit={handleAddCustomCurrency}>
            <ModalHeader>Add Custom Currency</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="bold">
                    Currency ISO Code (3-4 Letters)
                  </FormLabel>
                  <Input
                    placeholder="e.g. PLN, QAR, KWD, BRL, SGD, NZD"
                    maxLength={5}
                    value={newCurrency.code}
                    onChange={(e) =>
                      setNewCurrency({ ...newCurrency, code: e.target.value.toUpperCase().trim() })
                    }
                  />
                  <FormHelperText fontSize="xs">
                    Standard international 3-letter currency code (e.g. PLN for Polish Złoty).
                  </FormHelperText>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="bold">
                    Currency Symbol
                  </FormLabel>
                  <Input
                    placeholder="e.g. zł, ر.ق, KD, R$, S$"
                    maxLength={8}
                    value={newCurrency.symbol}
                    onChange={(e) => setNewCurrency({ ...newCurrency, symbol: e.target.value })}
                  />
                  <FormHelperText fontSize="xs">
                    Symbol shown on tickets, checkout, and invoices.
                  </FormHelperText>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel fontSize="sm" fontWeight="bold">
                    Currency Full Name
                  </FormLabel>
                  <Input
                    placeholder="e.g. Polish Zloty (PLN)"
                    value={newCurrency.name}
                    onChange={(e) => setNewCurrency({ ...newCurrency, name: e.target.value })}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="bold">
                    Country Flag / Emoji (Optional)
                  </FormLabel>
                  <Input
                    placeholder="e.g. 🇵🇱, 🇶🇦, 🇰🇼"
                    value={newCurrency.flag}
                    onChange={(e) => setNewCurrency({ ...newCurrency, flag: e.target.value })}
                  />
                </FormControl>

                {newCurrency.code && (
                  <Box
                    w="100%"
                    p={3}
                    borderRadius="md"
                    bg={previewBg}
                    borderWidth="1px"
                    borderColor="purple.200"
                  >
                    <Text fontSize="xs" fontWeight="bold" color="purple.700" _dark={{ color: 'purple.300' }}>
                      Preview Display:
                    </Text>
                    <Text fontSize="md" fontWeight="bold">
                      {newCurrency.flag || '🌐'} 100.00 {newCurrency.symbol || newCurrency.code} ({newCurrency.name || newCurrency.code})
                    </Text>
                  </Box>
                )}
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="purple" type="submit">
                Save Currency
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </VStack>
  );
}
